/** An app's store page — open to everyone. Picking a plan shows its price and
 *  exactly what it includes; "Get" asks for sign-in only now. */
import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { assetUrl, getApp, getAppLimits, money, mktError, type AppDetail, type Basis, type LimitRow } from '@/api/marketplace'
import { useAuth } from '@/auth/AuthContext'
import MarketplaceShell, { AppTile, Check, LimitsList, Stepper } from './MarketplaceShell'

export default function MarketplaceAppPage() {
  const { slug = '' } = useParams()
  const navigate = useNavigate()
  const { authStatus } = useAuth()
  const [app, setApp] = useState<AppDetail | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [basis, setBasis] = useState<Basis>('person')
  const [people, setPeople] = useState(10)
  const [size, setSize] = useState<string | null>(null)
  const [limits, setLimits] = useState<LimitRow[] | null>(null)

  useEffect(() => {
    getApp(slug)
      .then((a) => {
        setApp(a)
        if (!a.person && a.server.length) setBasis('server')
        if (a.server.length) setSize(a.server[Math.min(1, a.server.length - 1)].id)
      })
      .catch((e) => setError(mktError(e, "We couldn't find that app.").message))
  }, [slug])

  useEffect(() => {
    if (!app) return
    const t = window.setTimeout(() => {
      getAppLimits(slug, basis, basis === 'person' ? people : undefined, basis === 'server' ? size || undefined : undefined)
        .then((r) => setLimits(r.limits))
        .catch(() => setLimits(null))
    }, 250)
    return () => window.clearTimeout(t)
  }, [app, slug, basis, people, size])

  const chosenServer = app?.server.find((s) => s.id === size) || null
  const monthly = basis === 'person'
    ? (app?.person ? app.person.price_minor * people : null)
    : (chosenServer?.price_minor ?? null)

  // Point out the cheaper option for this team size.
  const tip = useMemo(() => {
    if (!app) return null
    if (basis === 'person' && app.person) {
      const fit = app.server.find((s) => s.max_people >= people && s.price_minor < app.person!.price_minor * people)
      if (fit) return { text: `For ${people} people, a ${fit.name} server costs less: ${money(fit.price_minor, app.currency)} a month instead of ${money(app.person.price_minor * people, app.currency)}.`, action: `Switch to the ${fit.name} server`, go: () => { setBasis('server'); setSize(fit.id) } }
    }
    if (basis === 'server' && app.person && chosenServer) {
      const n = Math.min(chosenServer.max_people, people)
      if (app.person.price_minor * n < chosenServer.price_minor) {
        return { text: `For ${n} people, paying per person costs ${money(app.person.price_minor * n, app.currency)} a month.`, action: 'Pay per person instead', go: () => setBasis('person') }
      }
    }
    return null
  }, [app, basis, people, chosenServer])

  const getIt = () => {
    const params = new URLSearchParams({ basis })
    if (basis === 'person') params.set('people', String(people))
    if (basis === 'server' && size) params.set('size', size)
    const target = `/marketplace/install/${slug}?${params.toString()}`
    if (authStatus === 'authed') navigate(target)
    else navigate(`/?next=${encodeURIComponent(target)}`)
  }

  if (error) {
    return (
      <MarketplaceShell>
        <div className="mkt-wrap" style={{ padding: '48px 20px' }}>
          <div className="mkt-empty"><strong style={{ fontSize: 18 }}>{error}</strong><Link to="/marketplace" className="btn btn-primary">Back to the marketplace</Link></div>
        </div>
      </MarketplaceShell>
    )
  }
  if (!app) return <MarketplaceShell><div className="mkt-wrap mkt-muted" style={{ padding: 40 }}>Loading…</div></MarketplaceShell>

  return (
    <MarketplaceShell>
      <div className="mkt-wrap" style={{ padding: '18px 20px 0' }}>
        <nav aria-label="Breadcrumb" className="mkt-row mkt-muted" style={{ fontSize: 14, gap: 8 }}>
          <Link to="/marketplace" className="mkt-muted">Marketplace</Link><span aria-hidden="true">›</span>
          <span>{app.category}</span><span aria-hidden="true">›</span>
          <span style={{ color: 'var(--color-text)' }}>{app.name}</span>
        </nav>
      </div>
      <div className="mkt-wrap mkt-split" style={{ padding: '18px 20px 40px' }}>
        <div className="mkt-main">
          <section className="mkt-row" style={{ gap: 18, alignItems: 'flex-start', flexWrap: 'nowrap' }}>
            <AppTile color={app.icon_color} text={app.initials} size={80} />
            <div className="mkt-col" style={{ gap: 6 }}>
              <h1 style={{ fontSize: 36 }}>{app.name}</h1>
              <span className="mkt-muted">by <strong style={{ color: 'var(--color-text)' }}>{app.publisher}</strong></span>
              <p style={{ margin: 0, fontSize: 17 }}>{app.tagline}</p>
            </div>
          </section>

          {app.screenshots.length > 0 && (
            <section aria-label="Screenshots" className="mkt-shots">
              {app.screenshots.map((s) => (
                <figure key={s.id} style={{ margin: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <img src={assetUrl(s.url)} alt={s.caption || `${app.name} screenshot`} loading="lazy" />
                  {s.caption && <figcaption className="mkt-faint">{s.caption}</figcaption>}
                </figure>
              ))}
            </section>
          )}

          <section className="mkt-col">
            <h2 style={{ fontSize: 22 }}>What your team can do</h2>
            <div style={{ whiteSpace: 'pre-line', fontSize: 16 }}>{app.description}</div>
          </section>

          <section className="mkt-col">
            <div className="mkt-row" style={{ justifyContent: 'space-between', alignItems: 'baseline' }}>
              <h2 style={{ fontSize: 22 }}>What's included in your plan</h2>
              <span className="mkt-muted" style={{ fontSize: 14 }}>For: <strong style={{ color: 'var(--color-text)' }}>{basis === 'person' ? `${people} ${people === 1 ? 'person' : 'people'}` : `${chosenServer?.name ?? ''} server`}</strong></span>
            </div>
            <p className="mkt-muted" style={{ margin: 0 }}>Your app runs on Twasta's servers. These limits keep it fast for you and everyone else, and change with the plan you pick.</p>
            {limits ? <LimitsList rows={limits} /> : <div className="mkt-muted">Working out the limits…</div>}
            <div className="mkt-banner info">
              <div>
                <strong>What if we reach a limit?</strong>
                We email you at 80%. At the limit only adding new records pauses — everything you already have keeps working, and you can move to a bigger plan in one click. If your team works faster than the speed limit for a moment, the app simply asks them to wait a few seconds.
              </div>
            </div>
          </section>

          <section className="mkt-col" style={{ gap: 10 }}>
            <h2 style={{ fontSize: 22 }}>Questions</h2>
            <details className="mkt-panel" style={{ padding: 16 }}>
              <summary style={{ fontWeight: 600, cursor: 'pointer' }}>Can I cancel any time?</summary>
              <p className="mkt-muted" style={{ margin: '10px 0 0' }}>Yes, from My apps. The app keeps working until the end of the month you've paid for, and nothing more is charged. For {app.retention.readonly_days} days after that you can still download everything; your data is deleted for good after {app.retention.delete_after_days} days.</p>
            </details>
            <details className="mkt-panel" style={{ padding: 16 }}>
              <summary style={{ fontWeight: 600, cursor: 'pointer' }}>Who can see our data?</summary>
              <p className="mkt-muted" style={{ margin: '10px 0 0' }}>Only the people you invite. Your organization gets its own private database — other customers of this app never share it.</p>
            </details>
            <details className="mkt-panel" style={{ padding: 16 }}>
              <summary style={{ fontWeight: 600, cursor: 'pointer' }}>How do we pay?</summary>
              <p className="mkt-muted" style={{ margin: '10px 0 0' }}>Each month's price is taken from your organization wallet, which you top up by UPI or card. We remind you a week before it runs low, and if a payment is missed the app keeps working for {app.grace_days} more days.</p>
            </details>
            <details className="mkt-panel" style={{ padding: 16 }}>
              <summary style={{ fontWeight: 600, cursor: 'pointer' }}>Are taxes included?</summary>
              <p className="mkt-muted" style={{ margin: '10px 0 0' }}>Prices are shown before tax. GST is added when you top up your wallet, and you see the exact total before you pay.</p>
            </details>
          </section>

          <section className="mkt-panel mkt-row" style={{ justifyContent: 'space-between' }}>
            <div className="mkt-col" style={{ gap: 2 }}>
              <span className="mkt-muted" style={{ fontSize: 14 }}>Made and supported by</span>
              <strong style={{ fontSize: 17 }}>{app.publisher}</strong>
            </div>
            {app.support_email && <a className="btn btn-secondary" href={`mailto:${app.support_email}`}>Contact the publisher</a>}
          </section>
        </div>

        <aside className="mkt-aside mkt-panel" aria-label="Choose your plan" style={{ position: 'sticky', top: 76 }}>
          <h2>Choose your plan</h2>
          {app.person && app.server.length > 0 && (
            <div role="group" aria-label="How to pay" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, background: 'var(--color-raised, var(--color-bg))', padding: 5, borderRadius: 12 }}>
              {(['person', 'server'] as Basis[]).map((b) => (
                <button key={b} type="button" aria-pressed={basis === b} onClick={() => setBasis(b)}
                  className="btn" style={{ background: basis === b ? 'var(--color-bg-secondary)' : 'transparent', color: 'var(--color-text)', boxShadow: basis === b ? '0 1px 3px rgba(0,0,0,0.12)' : 'none' }}>
                  {b === 'person' ? 'Per person' : 'Per server'}
                </button>
              ))}
            </div>
          )}
          {basis === 'person' && app.person && (
            <div className="mkt-col" style={{ gap: 6 }}>
              <span>How many people will use it?</span>
              <div className="mkt-row"><Stepper value={people} onChange={setPeople} label="people" max={10000} /><span className="mkt-muted">× {money(app.person.price_minor, app.currency)} a month</span></div>
            </div>
          )}
          {basis === 'server' && (
            <div role="radiogroup" aria-label="Server size" className="mkt-choices">
              {app.server.map((s) => (
                <button key={s.id} type="button" role="radio" aria-checked={size === s.id} onClick={() => setSize(s.id)} className={`mkt-choice mkt-choice-row${size === s.id ? ' on' : ''}`}>
                  <span className="mkt-col" style={{ gap: 0 }}><strong>{s.name} server</strong><span className="mkt-muted" style={{ fontSize: 14 }}>Up to {s.max_people} people</span></span>
                  <strong>{money(s.price_minor, app.currency)}<span className="mkt-faint"> /mo</span></strong>
                </button>
              ))}
            </div>
          )}
          <div style={{ borderTop: '1px solid var(--color-border)', paddingTop: 14 }}>
            <div className="mkt-row" style={{ justifyContent: 'space-between', alignItems: 'baseline' }}>
              <span>Monthly price</span><strong style={{ fontSize: 28 }}>{money(monthly, app.currency)}</strong>
            </div>
            <div className="mkt-faint" style={{ textAlign: 'right' }}>+ GST · billed every month</div>
          </div>
          {tip && (
            <div className="mkt-banner warn" style={{ padding: 12 }}>
              <div className="mkt-col" style={{ gap: 4 }}>
                <span style={{ fontSize: 14 }}>{tip.text}</span>
                <button type="button" className="btn btn-ghost btn-sm" style={{ alignSelf: 'flex-start', paddingLeft: 0 }} onClick={tip.go}>{tip.action}</button>
              </div>
            </div>
          )}
          {app.trial ? (
            <div className="mkt-banner ok" style={{ padding: 12 }}>
              <div><strong>Free for {app.trial.days} days</strong>for up to {app.trial.people} people. You won't pay anything today, and we remind you before the trial ends.</div>
            </div>
          ) : (
            <div className="mkt-banner plain" style={{ padding: 12 }}>
              <div><strong>No free trial right now.</strong>You pay from the day you install, and can cancel any time.</div>
            </div>
          )}
          <button type="button" className="btn btn-primary btn-lg" style={{ width: '100%' }} onClick={getIt} disabled={monthly === null}>Get {app.name}</button>
          <ul className="mkt-col" style={{ listStyle: 'none', padding: 0, margin: 0, gap: 8, fontSize: 14 }}>
            {['Hosting, updates and security included', 'Your own private database', 'Change plan or cancel any time'].map((t) => (
              <li key={t} className="mkt-row" style={{ gap: 8 }}><span style={{ color: 'var(--color-success)' }}><Check /></span>{t}</li>
            ))}
          </ul>
          {authStatus !== 'authed' && <span className="mkt-faint" style={{ textAlign: 'center' }}>You'll sign in to Twasta before anything is installed.</span>}
        </aside>
      </div>
    </MarketplaceShell>
  )
}
